import {
  addDays,
  addMonths,
  addYears,
  format,
  isAfter,
  isBefore,
  isFirstDayOfMonth,
  lastDayOfMonth,
  setDate,
  subDays,
  subMonths,
} from 'date-fns';
import {
  EligibilityDates,
  MedSuppEligibilityDates,
  TaxFilingStatus,
} from 'types/user';
import { isValidDateString } from 'utils/dobHelpers';
import {
  CostTier,
  IndividualTiers,
  MarriedJointlyTiers,
  MarriedSeparatelyTiers,
} from './CMSData';

const calculateStartDate = (date: Date, bornFirstDay: boolean): Date => {
  const monthsBefore: number = bornFirstDay ? 4 : 3;
  return setDate(subMonths(date, monthsBefore), 1);
};

const calculateEndDate = (date: Date, bornFirstDay: boolean): Date => {
  const monthsAhead: number = bornFirstDay ? 2 : 3;
  return lastDayOfMonth(addMonths(date, monthsAhead));
};

const calculateFirstCoverage = (date: Date, bornFirstDay: boolean): Date => {
  if (bornFirstDay) {
    return subMonths(date, 1);
  }

  return setDate(date, 1);
};

const calculateEarlyCutoff = (date: Date, bornFirstDay: boolean): Date => {
  const monthsBefore: number = bornFirstDay ? 2 : 1;
  return lastDayOfMonth(subMonths(date, monthsBefore));
};

export function calculateEligibilityDates(birthday: Date): EligibilityDates {
  const bornFirstDay: boolean = isFirstDayOfMonth(birthday);
  const sixtyFive: Date = addYears(birthday, 65);
  const iepStartDate = calculateStartDate(sixtyFive, bornFirstDay);
  const iepEndDate = calculateEndDate(sixtyFive, bornFirstDay);

  return {
    bornFirstDay,
    earlyIEPCutoff: calculateEarlyCutoff(sixtyFive, bornFirstDay),
    firstCoverageStartDate: calculateFirstCoverage(sixtyFive, bornFirstDay),
    iepEndDate,
    iepStartDate,
  };
}

const parseTiers = (income: number, tiers: CostTier[]): CostTier | null => {
  const found: CostTier | undefined = tiers.find((tier) => {
    return income >= tier.low && income <= tier.high ? true : false;
  });

  return found || null;
};

export function getFilingStatus(
  isMarried: boolean,
  filingStatus: string
): string {
  if (filingStatus === 'jointly') {
    return TaxFilingStatus.MarriedFilingJointly;
  }

  if (isMarried) {
    return TaxFilingStatus.MarriedFilingSeparately;
  }

  return TaxFilingStatus.Single;
}

export function calculateMedicarePartBCosts(
  income: number,
  taxFilingStatus: string
): number {
  let tier = null;

  if (taxFilingStatus === TaxFilingStatus.MarriedFilingJointly) {
    tier = parseTiers(income, MarriedJointlyTiers);
  } else if (taxFilingStatus === TaxFilingStatus.MarriedFilingSeparately) {
    tier = parseTiers(income, MarriedSeparatelyTiers);
  } else {
    tier = parseTiers(income, IndividualTiers);
  }

  return tier?.cost || 0;
}

export function calculateMedicarePartDCosts(
  income: number,
  taxFilingStatus: string
): number {
  let tier = null;

  if (taxFilingStatus === TaxFilingStatus.MarriedFilingJointly) {
    tier = parseTiers(income, MarriedJointlyTiers);
  } else if (taxFilingStatus === TaxFilingStatus.MarriedFilingSeparately) {
    tier = parseTiers(income, MarriedSeparatelyTiers);
  } else {
    tier = parseTiers(income, IndividualTiers);
  }

  return tier?.partD || 0;
}

export function formattedEligibilityDates(birthdate: string) {
  if (!isValidDateString(birthdate)) return {};

  const {
    bornFirstDay,
    earlyIEPCutoff,
    firstCoverageStartDate,
    iepEndDate,
    iepStartDate,
  } = calculateEligibilityDates(new Date(birthdate));

  return {
    bornFirstDay,
    earlyIEPCutoff: format(earlyIEPCutoff, 'MM/dd/yyyy'),
    earlyIEPCutoffMonth: format(earlyIEPCutoff, 'LLLL'),
    firstCoverageStartDate: format(firstCoverageStartDate, 'MM/dd/yyyy'),
    iepEndDate: format(iepEndDate, 'MM/dd/yyyy'),
    iepEndDateMonth: format(iepEndDate, 'LLLL'),
    iepStartDate: format(iepStartDate, 'MM/dd/yyyy'),
    iepStartDateMonth: format(iepStartDate, 'LLLL'),
  };
}

export function getEligibilityStatus(startDate: Date, endDate: Date) {
  const today = new Date();

  if (isBefore(today, startDate)) {
    return 'before';
  }

  if (isAfter(today, endDate)) {
    return 'past';
  }

  return 'during';
}

export function getIEPStatus(DOB: Date) {
  const { iepStartDate, iepEndDate } = calculateEligibilityDates(DOB);

  const status = getEligibilityStatus(iepStartDate, iepEndDate);
  return `${status}IEP`;
}

export function inRange(date: Date, begin: Date, end: Date): boolean {
  const beginning = subDays(begin, 1);
  const ending = addDays(end, 1);

  return isAfter(date, beginning) && isBefore(date, ending);
}

const utcMonthDay = (date: Date): number =>
  (date.getUTCMonth() + 1) * 100 + date.getUTCDate();

// Inclusive UTC calendar-day window. `inRange` shifts each endpoint by a full
// day, so a start of Oct 15 00:00Z also matches the rest of Oct 14.
const inUtcMonthDayRange = (
  today: Date,
  startMonthDay: number,
  endMonthDay: number
): boolean => {
  const monthDay = utcMonthDay(today);
  return monthDay >= startMonthDay && monthDay <= endMonthDay;
};

export function getEnrollmentWindow(today: Date): string {
  // MA OEP:  Jan 1 to March 31
  if (inUtcMonthDayRange(today, 101, 331)) {
    return 'MA OEP';
  }

  // SEP after GEP: April 1 - June 30
  if (inUtcMonthDayRange(today, 401, 630)) {
    return 'SEP after GEP';
  }

  // AEP:  October 15 - Dec 7
  if (inUtcMonthDayRange(today, 1015, 1207)) {
    return 'AEP';
  }

  return 'none';
}

export function getMedSuppEligibilityDates(
  birthday: Date
): MedSuppEligibilityDates {
  const bornFirstDay: boolean = isFirstDayOfMonth(birthday);
  const sixtyFive: Date = addYears(birthday, 65);

  return {
    startDate: calculateStartDate(sixtyFive, bornFirstDay),
    endDate: lastDayOfMonth(addMonths(sixtyFive, 6)),
  };
}

export function inMedSuppIEP(today: Date, birthday: Date): boolean {
  const iep = getMedSuppEligibilityDates(birthday);
  return inRange(today, iep.startDate, iep.endDate);
}

export function inMedSuppSpecialEnrollment(
  today: Date,
  coverageLoss: Date
): boolean {
  const startDate = subMonths(coverageLoss, 3);
  const endDate = addMonths(coverageLoss, 6);

  return inRange(today, startDate, endDate);
}

export function isMedSuppYearRound(state: string): boolean {
  return ['NY', 'CT'].includes(state);
}

export function getMedSuppEnrollmentWindow(
  today: Date,
  birthdate: Date,
  _state: string // eslint-disable-line @typescript-eslint/no-unused-vars
): string {
  // First, check if user is in Initial Enrollment Period (IEP)
  // IEP takes priority as users in IEP can enroll at any point during the year
  if (inMedSuppIEP(today, birthdate)) {
    return 'IEP';
  }

  // If not in IEP, check for other enrollment windows
  // This is used by MedSuppOverlappingPathfinder to handle overlapping windows
  const enrollmentWindow = getEnrollmentWindow(today);
  if (enrollmentWindow !== 'none') {
    return enrollmentWindow;
  }

  // Put full state checks here at some point
  // PA doesn't have enrollment past IEP, so sucks for them
  // return 'SEP' when we implement proper state checks

  return 'none';
}

export function eligibleConverageTypes(
  today: Date,
  birthdate: Date,
  state: string
): string {
  const eligible = [];

  // is eligible for MA
  const maEnrollmentWindow = getEnrollmentWindow(today);
  if (['MA OEP', 'AEP'].includes(maEnrollmentWindow)) {
    eligible.push('MA');
  }

  const medSuppEnrollmentWindow = getMedSuppEnrollmentWindow(
    today,
    birthdate,
    state
  );
  if (['IEP', 'SEP'].includes(medSuppEnrollmentWindow)) {
    eligible.push('Med Supp');
  }

  return eligible.join(' + ');
}
